import { ComponentType, Fragment, h } from "preact"
import { KeywordInCategory, KeywordInCategoryProps } from "./KeywordInCategory"
import { KeywordsInCategory } from "./KeywordsInCategory"
import { ElementProps, KeywordProps } from "./types"
import type { SearchKeyword, SearchTermsFacet } from "@nosto/nosto-js/client"

/**
 * @group Components
 */
export interface KeywordsProps {
    /**
     * Keywords to render.
     */
    hits: SearchKeyword[]
    /**
     * Component for rendering keyword's visual content.
     */
    keywordComponent: ComponentType<KeywordProps>
    /**
     * Component for rendering single keyword element.
     */
    elementComponent?: ComponentType<ElementProps>
    /**
     * Component for rendering keyword with category suggestion (default: KeywordInCategory)
     */
    keywordInCategoryComponent?: ComponentType<KeywordInCategoryProps>
    /**
     * Enable category suggestion (default: true)
     */
    categorySuggestion?: boolean
    /**
     * Number of category suggestions to render (default: 3)
     */
    categorySuggestionCount?: number
}

/**
 * Component for keywords rendering.
 *
 * @example
 * ```jsx
 * <Keywords
 *    hits={keywords.hits}
 *    keywordComponent={({ hit }) => <span
 *        data-nosto-element="keyword"
 *    >
 *        {hit?._highlight?.keyword
 *            ? <span dangerouslySetInnerHTML={{ __html: hit._highlight.keyword }}></span>
 *            : hit.keyword
 *        }
 *    </span>}
 *    elementComponent={({ hit, keywordComponent, filter }) =>
 *        <AutocompleteElement hit={hit} filter={filter} class="ns-clickable ns-py-2 ns-px-4 ns-font-4">
 *            {keywordComponent}
 *        </AutocompleteElement>
 *    }
 * />
 * ```
 *
 * @group Components
 */
export function Keywords({
    hits,
    elementComponent: Element,
    keywordComponent: Keyword,
    keywordInCategoryComponent = KeywordInCategory,
    categorySuggestion = true,
    categorySuggestionCount = 3
}: KeywordsProps) {
    const [firstKeyword, ...restKeywords] = hits ?? []
    // Find first facet's field value
    const categoryField = hits
        ?.find(hit =>
            hit?.facets?.find((facet): facet is SearchTermsFacet => "data" in facet && facet?.data?.length > 0)
        )
        ?.facets?.find((facet): facet is SearchTermsFacet => "data" in facet && facet?.data?.length > 0)?.field

    if (categoryField && firstKeyword && categorySuggestion) {
        return (
            <Fragment>
                <KeywordsInCategory
                    hit={firstKeyword}
                    categoryField={categoryField}
                    keywordComponent={<Keyword hit={firstKeyword} />}
                    wrapperComponent={({ keywordComponent, filter }) => {
                        return Element ? (
                            <Element keywordComponent={keywordComponent} hit={firstKeyword} filter={filter} />
                        ) : (
                            <Keyword hit={firstKeyword} />
                        )
                    }}
                    keywordInCategoryComponent={keywordInCategoryComponent}
                    categorySuggestionCount={categorySuggestionCount}
                />
                {restKeywords?.map((hit, index) => {
                    return Element ? (
                        <Element key={`${hit.keyword}-${index}}`} keywordComponent={<Keyword hit={hit} />} hit={hit} />
                    ) : (
                        <Keyword hit={hit} />
                    )
                })}
            </Fragment>
        )
    }

    return hits?.map((hit, index) => {
        return Element ? (
            <Element key={`${hit.keyword}-${index}}`} keywordComponent={<Keyword hit={hit} />} hit={hit} />
        ) : (
            <Keyword hit={hit} />
        )
    })
}
